/**
 * Whether a question is open-ended rather than an A–D choice. Paper questions
 * the teacher marks (short answer, essay, working) keep the default
 * `multiple_choice` type with four blank options, so the type alone is not
 * enough: the `graded_by_teacher` flag, a non-choice type, or four blank
 * options all mean "no choices to show".
 */
const CHOICE_TYPES = new Set(['multiple_choice', 'true_false']);

const blank = (v: unknown): boolean =>
  String(v ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';

export function isOpenQuestion(q: Record<string, any> | null | undefined): boolean {
  if (!q) return false;
  if (q.graded_by_teacher) return true;
  const type = q.question_type ?? q.type;
  if (type && !CHOICE_TYPES.has(type)) return true;
  const options: unknown[] = Array.isArray(q.options)
    ? q.options.map((o: any) => (o && typeof o === 'object' ? o.text : o))
    : [q.option1 ?? q.choice_a, q.option2 ?? q.choice_b, q.option3 ?? q.choice_c, q.option4 ?? q.choice_d];
  return options.length > 0 && options.every(blank);
}

/** The card label for an open question: "Marked by teacher" on paper, else its kind. */
export function openQuestionLabel(q: Record<string, any>): string {
  if (q.graded_by_teacher) return 'Marked by teacher';
  switch (q.question_type ?? q.type) {
    case 'written': return 'Written answer';
    case 'fill_blank': return 'Fill in the blank';
    case 'matching': return 'Matching';
    default: return 'Short answer';
  }
}
